/**
 * Draggable Video Scrubber
 * Allows users to drag/scrub through video playback
 */

class DraggableVideo {
  constructor(videoElement) {
    this.video = videoElement;
    this.container = videoElement.closest('.rich-text-with-media') || videoElement.parentElement;
    this.isDragging = false;
    this.wasPaused = false;
    this.startX = 0;
    this.startTime = 0;
    
    this.init();
  }

  init() {
    // Add draggable class for styling
    this.video.classList.add('draggable-video');
    
    // Mouse events
    this.video.addEventListener('mousedown', this.onDragStart.bind(this));
    document.addEventListener('mousemove', this.onDragMove.bind(this));
    document.addEventListener('mouseup', this.onDragEnd.bind(this));
    
    // Touch events for mobile
    this.video.addEventListener('touchstart', this.onDragStart.bind(this));
    document.addEventListener('touchmove', this.onDragMove.bind(this));
    document.addEventListener('touchend', this.onDragEnd.bind(this));
    
    // Prevent default video controls behavior
    this.video.addEventListener('click', (e) => {
      if (this.isDragging) {
        e.preventDefault();
      }
    });
  }

  onDragStart(e) {
    this.isDragging = true;
    this.wasPaused = this.video.paused;
    
    // Get starting position
    if (e.type === 'mousedown') {
      this.startX = e.clientX;
    } else if (e.type === 'touchstart') {
      this.startX = e.touches[0].clientX;
      e.preventDefault(); // Prevent scrolling on mobile
    }
    
    this.startTime = this.video.currentTime;
    this.video.pause();
    
    // Add visual feedback
    this.video.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    
    e.preventDefault();
    
    let currentX;
    if (e.type === 'mousemove') {
      currentX = e.clientX;
    } else if (e.type === 'touchmove') {
      currentX = e.touches[0].clientX;
    }
    
    // Calculate drag distance
    const deltaX = currentX - this.startX;
    
    // Much more responsive - amplify the movement
    const sensitivity = 4; // Multiplier for responsiveness (higher = more sensitive)
    const videoWidth = this.video.offsetWidth;
    const dragPercent = (deltaX / videoWidth) * sensitivity;
    
    // Calculate new time based on percentage of video duration
    let newTime = this.startTime + (dragPercent * this.video.duration);
    
    // Loop the time if it goes beyond bounds
    if (newTime < 0) {
      newTime = this.video.duration + (newTime % this.video.duration);
    } else if (newTime > this.video.duration) {
      newTime = newTime % this.video.duration;
    }
    
    // Update video time
    this.video.currentTime = newTime;
  }

  onDragEnd() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    
    // Resume playing if it was playing before
    if (!this.wasPaused) {
      this.video.play();
    }
    
    // Reset cursor
    this.video.style.cursor = 'grab';
    document.body.style.userSelect = '';
  }
}

// Lazy load video when it enters viewport
function initLazyVideo(video) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && video.dataset.src) {
        // Load the video
        video.src = video.dataset.src;
        video.load();
        
        // Play when loaded
        video.addEventListener('loadeddata', () => {
          video.play().catch(err => {
            console.log('Autoplay prevented:', err);
          });
        }, { once: true });
        
        // Stop observing after loading
        observer.unobserve(video);
      }
    });
  }, {
    rootMargin: '50px' // Start loading 50px before entering viewport
  });
  
  observer.observe(video);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Find the spinning sign video (simple version - no dragging)
  const simpleVideos = document.querySelectorAll('.spinning-sign-video-simple');
  simpleVideos.forEach(video => {
    // Just lazy load, no dragging
    initLazyVideo(video);
  });
  
  // Find draggable videos (if any other videos need drag functionality)
  const draggableVideos = document.querySelectorAll('.spinning-sign-video');
  draggableVideos.forEach(video => {
    // Initialize lazy loading
    initLazyVideo(video);
    
    // Initialize draggable functionality
    new DraggableVideo(video);
  });
});

